import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getAiSuggestion,
  getAiSuggestionHistory,
  type AiSuggestionHistory,
} from '../api/inquiryChat';
import type { AiBatchResultItem } from '../components/Chat/AiSuggestionModal';
import { AI_SELECT_LIMIT } from '../constants';
import { messageStrings } from '../constants/strings';

export function useInquiryAiSuggestions(channel: string) {
  const [selectedMessages, setSelectedMessages] = useState<Map<string, string>>(
    new Map(),
  );
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [aiModalResults, setAiModalResults] = useState<AiBatchResultItem[]>([]);
  const [aiBatchLoading, setAiBatchLoading] = useState(false);
  const lastBatchRef = useRef<[string, string][]>([]);
  const [aiHistoryOpen, setAiHistoryOpen] = useState(false);
  const [aiHistoryItems, setAiHistoryItems] = useState<AiSuggestionHistory[]>(
    [],
  );
  const [aiHistoryLoading, setAiHistoryLoading] = useState(false);

  const selectedTimetokens = useMemo(
    () => new Set(selectedMessages.keys()),
    [selectedMessages],
  );

  useEffect(() => {
    setSelectedMessages(new Map());
    setAiModalOpen(false);
    setAiModalResults([]);
    setAiBatchLoading(false);
    setAiHistoryOpen(false);
    setAiHistoryItems([]);
    setAiHistoryLoading(false);
    lastBatchRef.current = [];
  }, [channel]);

  const handleToggleSelect = useCallback((timetoken: string, text: string) => {
    setSelectedMessages((prev) => {
      const next = new Map(prev);
      if (next.has(timetoken)) {
        next.delete(timetoken);
      } else if (next.size < AI_SELECT_LIMIT) {
        next.set(timetoken, text);
      }

      return next;
    });
  }, []);

  const runBatchGenerate = useCallback(async (entries: [string, string][]) => {
    if (entries.length === 0) return;
    lastBatchRef.current = entries;
    setAiModalOpen(true);
    setAiModalResults([]);
    setAiBatchLoading(true);

    const settled = await Promise.allSettled(
      entries.map(async ([timetoken, text]) => {
        const result = await getAiSuggestion({
          channelId: channel,
          message: text,
          messageTimetoken: timetoken,
        });
        return {
          timetoken,
          originalMessage: text,
          answer: result.answer || messageStrings.aiSuggestNoAnswer,
        } as AiBatchResultItem;
      }),
    );

    const results: AiBatchResultItem[] = settled.map((s, i) =>
      s.status === 'fulfilled'
        ? s.value
        : {
            timetoken: entries[i][0],
            originalMessage: entries[i][1],
            answer: messageStrings.aiSuggestFailed,
          },
    );

    setAiModalResults(results);
    setAiBatchLoading(false);
  }, [channel]);

  const handleBatchGenerate = useCallback(async () => {
    const entries = Array.from(selectedMessages.entries()).filter(
      ([, text]) => !!text.trim(),
    );
    if (entries.length === 0) return;

    setSelectedMessages(new Map());
    await runBatchGenerate(entries);
  }, [selectedMessages, runBatchGenerate]);

  const handleRegenerate = useCallback(async () => {
    const entries = lastBatchRef.current;
    if (entries.length === 0) return;

    await runBatchGenerate(entries);
  }, [runBatchGenerate]);

  const handleShowAiHistory = useCallback(async (timetoken: string) => {
    setAiHistoryOpen(true);
    setAiHistoryLoading(true);
    setAiHistoryItems([]);
    try {
      const history = await getAiSuggestionHistory(channel, timetoken);
      setAiHistoryItems(history);
    } catch {
      setAiHistoryItems([]);
    } finally {
      setAiHistoryLoading(false);
    }
  }, [channel]);

  return {
    selectedTimetokens,
    aiSelectedCount: selectedMessages.size,
    aiBatchLoading,
    handleToggleSelect,
    handleBatchGenerate,
    handleShowAiHistory,
    aiModalOpen,
    aiModalResults,
    handleRegenerate,
    closeSuggestionModal: () => {
      setAiModalOpen(false);
      setAiModalResults([]);
    },
    aiHistoryOpen,
    aiHistoryItems,
    aiHistoryLoading,
    closeHistoryModal: () => {
      setAiHistoryOpen(false);
      setAiHistoryItems([]);
    },
  };
}
